/**
 * Unified client-side site search.
 *
 * Why client-side: the backend `/api/search` only queries Mongo collections that
 * hold no public content, so it returns `{}` for every query. The real content
 * (catalogue constants, curriculum tree, crafts, wiki, hospitality menu, vocab
 * decks) is published as a small sharded JSON index by
 * `scripts/search/build-search-index.mjs`, which this module loads and scores in
 * the browser. Zero dependencies, no framework imports — the same file is copied
 * verbatim into the chefkunming / omniversail / visit-kunming repos.
 *
 * Index layout (same-origin, or any base passed in):
 *   <base>/<site>/manifest.json   { site, label, total, shards:[{id,label,file}] }
 *   <base>/<site>/<shard>.json    { id, label, docs:[{id,title,text,url,image,tags}] }
 *   <base>/federation.json        { sites:[{id,label,base,manifest}] }  (cross-site)
 */

const DEFAULT_BASE = '/data/search';
const MIN_QUERY = 2;

/* ── scoring ─────────────────────────────────────────────────────────────── */

const CJK = /[㐀-䶿一-鿿぀-ヿ가-힯]/;

/** Normalise: lowercase + NFKC + collapse whitespace. */
export function normalizeText(v) {
  return String(v == null ? '' : v)
    .normalize('NFKC')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Split a query into match terms.
 *
 * Latin words are REQUIRED (AND): "bike yunnan" must hit both.
 * CJK has no spaces, so a CJK chunk is expanded into the chunk itself plus every
 * bigram and all of those are OPTIONAL (OR, at least one must hit) — otherwise
 * "云南美食" could never match a title written as "云南 美食".
 *
 * @returns {Array<{text:string, required:boolean, weight:number}>}
 */
export function buildTerms(query) {
  const q = normalizeText(query);
  if (!q) return [];
  const terms = [];
  const chunks = q.split(/[^\p{L}\p{N}]+/u).filter(Boolean);
  for (const c of chunks) {
    const text = c.replace(/^['’]+|['’]+$/g, '');
    if (!text) continue;
    if (CJK.test(text)) {
      terms.push({ text, required: false, weight: 1 });
      for (let i = 0; i + 2 <= text.length && i < 16; i += 1) {
        terms.push({ text: text.slice(i, i + 2), required: false, weight: 0.5 });
      }
    } else {
      terms.push({ text, required: true, weight: 1 });
    }
  }
  // The whole CJK phrase (spaces removed) is a strong optional signal: it only
  // fires when the target really contains the phrase verbatim.
  const compact = q.replace(/[^\p{L}\p{N}]/gu, '');
  if (CJK.test(compact) && compact.length >= 2 && !terms.some((t) => t.text === compact)) {
    terms.push({ text: compact, required: false, weight: 2 });
  }
  return terms;
}

/** Flat list of term strings (highlighting / debugging). */
export function tokenizeQuery(query) {
  return buildTerms(query).map((t) => t.text);
}

function toTerms(terms) {
  return (terms || []).map((t) => (typeof t === 'string' ? { text: t, required: true, weight: 1 } : t));
}

/**
 * Score one document against terms.
 * @returns {number} 0 when the document does not match.
 */
export function scoreDoc(doc, terms) {
  const list = toTerms(terms);
  if (!doc || !list.length) return 0;
  const title = normalizeText(doc.title);
  const text = normalizeText(doc.text);
  const tags = (doc.tags || []).map(normalizeText).join(' ');
  let score = 0;
  let optionalHits = 0;
  let hasRequired = false;
  for (const term of list) {
    const t = normalizeText(term.text);
    if (!t) continue;
    let best = 0;
    if (title === t) best = 120;
    else if (title.startsWith(t)) best = 90;
    else if (wordStartsWith(title, t)) best = 60;
    else if (title.includes(t)) best = 40;
    if (tags.includes(t)) best = Math.max(best, 30);
    if (text.includes(t)) best = Math.max(best, 12);
    const weight = term.weight == null ? 1 : term.weight;
    if (term.required) {
      hasRequired = true;
      if (best === 0) return 0; // AND: every required term must hit somewhere
      score += best * weight;
    } else if (best > 0) {
      optionalHits += 1;
      score += best * weight;
    }
  }
  if (!hasRequired && optionalHits === 0) return 0;
  // Prefer tight titles over long blurb matches.
  if (title.length <= 40) score += 4;
  if (doc.image) score += 1;
  return score;
}

function wordStartsWith(haystack, token) {
  let i = haystack.indexOf(token);
  while (i !== -1) {
    if (i === 0 || /[^\p{L}\p{N}]/u.test(haystack[i - 1])) return true;
    i = haystack.indexOf(token, i + 1);
  }
  return false;
}

/**
 * Pure, synchronous search over an array of docs.
 * @returns {Array<{doc:object, score:number}>} best first
 */
export function searchDocs(query, docs, limit = 10) {
  const terms = buildTerms(query);
  if (terms.length === 0) return [];
  const hits = [];
  for (const doc of docs) {
    const score = scoreDoc(doc, terms);
    if (score > 0) hits.push({ doc, score });
  }
  hits.sort((a, b) => b.score - a.score || a.doc.title.length - b.doc.title.length);
  return limit > 0 ? hits.slice(0, limit) : hits;
}

/** Split `text` into `{text, hit}` segments for highlighting matched tokens. */
export function highlightParts(text, tokens) {
  const src = String(text == null ? '' : text);
  if (!src) return [];
  const list = (tokens || []).map(normalizeText).filter(Boolean);
  if (!list.length) return [{ text: src, hit: false }];
  const lower = normalizeText(src);
  // `normalizeText` is length-preserving except for whitespace collapse, so a
  // direct index scan on the lowered string is safe for display purposes.
  const flags = new Array(src.length).fill(false);
  for (const t of list) {
    let i = lower.indexOf(t);
    while (i !== -1) {
      for (let k = i; k < i + t.length && k < flags.length; k += 1) flags[k] = true;
      i = lower.indexOf(t, i + t.length);
    }
  }
  const out = [];
  let buf = '';
  let cur = flags[0];
  for (let i = 0; i < src.length; i += 1) {
    if (flags[i] === cur) {
      buf += src[i];
    } else {
      if (buf) out.push({ text: buf, hit: cur });
      buf = src[i];
      cur = flags[i];
    }
  }
  if (buf) out.push({ text: buf, hit: cur });
  return out;
}

/* ── index loading ───────────────────────────────────────────────────────── */

const cache = new Map(); // siteId -> { manifest, shards: Map<id, docs> }
const inflight = new Map();
const federationCache = new Map();

async function getJson(url, { timeoutMs = 12000 } = {}) {
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
  try {
    const res = await fetch(url, {
      signal: controller ? controller.signal : undefined,
      credentials: 'omit',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status} for ${url}`);
    return await res.json();
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * Resolve the manifest URL for a site.
 * `loc` is either an index root (`/data/search`, same-origin) or an absolute
 * manifest URL taken from `federation.json` (cross-origin sibling site).
 */
function manifestUrlFor(site, loc) {
  const l = String(loc || DEFAULT_BASE);
  if (l.endsWith('/manifest.json')) return l;
  return `${l.replace(/\/+$/, '')}/${site}/manifest.json`;
}

/**
 * Load (and memoise) one site's index. Safe to call repeatedly.
 * @param {string} site  site id, e.g. 'mgv'
 * @param {string} [loc] index root (`/data/search`) or absolute manifest URL
 * @param {string} [siteBase] origin of the site, used to build external links
 * @returns {Promise<{site:string,label:string,total:number,docs:Array}>}
 */
export async function loadSiteIndex(site, loc = DEFAULT_BASE, siteBase = '') {
  const manifestUrl = manifestUrlFor(site, loc);
  const key = manifestUrl;
  if (cache.has(key)) return cache.get(key);
  if (inflight.has(key)) return inflight.get(key);
  const root = manifestUrl.slice(0, manifestUrl.lastIndexOf('/'));
  const task = (async () => {
    const manifest = await getJson(manifestUrl);
    const shards = new Map();
    await Promise.all(
      (manifest.shards || []).map(async (s) => {
        try {
          const j = await getJson(`${root}/${s.file}`);
          shards.set(s.id, { label: s.label || s.id, docs: j?.docs || [] });
        } catch {
          shards.set(s.id, { label: s.label || s.id, docs: [] });
        }
      }),
    );
    const docs = [];
    for (const [id, shard] of shards) {
      for (const d of shard.docs) docs.push({ ...d, _shard: id, _shardLabel: shard.label, _site: site });
    }
    const entry = {
      site,
      label: manifest.label || site,
      base: siteBase || manifest.base || '',
      total: manifest.total ?? docs.length,
      docs,
    };
    cache.set(key, entry);
    return entry;
  })();
  inflight.set(key, task);
  try {
    return await task;
  } finally {
    inflight.delete(key);
  }
}

/** Load `<base>/federation.json` — the list of sibling sites to search. */
export async function loadFederation(base = DEFAULT_BASE) {
  if (federationCache.has(base)) return federationCache.get(base);
  try {
    const j = await getJson(`${String(base).replace(/\/+$/, '')}/federation.json`);
    const sites = Array.isArray(j?.sites) ? j.sites : [];
    federationCache.set(base, sites);
    return sites;
  } catch {
    federationCache.set(base, []);
    return [];
  }
}

/**
 * Search one or more sites.
 *
 * @param {string} query
 * @param {object} [opts]
 * @param {string} [opts.site='mgv']          own site id
 * @param {string[]} [opts.sites]             explicit site ids (defaults to own)
 * @param {string} [opts.base='/data/search']
 * @param {number} [opts.limit=20]
 * @param {boolean} [opts.federated=false]    also search sibling sites via federation.json
 * @returns {Promise<{query:string,groups:Array<{site,label,shard,shardLabel,hits:Array}>,total:number,error:string|null}>}
 */
export async function searchIndex(query, opts = {}) {
  const {
    site = 'mgv',
    sites = null,
    base = DEFAULT_BASE,
    limit = 20,
    federated = false,
  } = opts;
  const term = String(query || '').trim();
  const empty = { query: term, groups: [], total: 0, error: null };
  if (term.length < MIN_QUERY) return empty;

  // Own site is always same-origin; siblings are loaded from the absolute
  // manifest URLs published in federation.json.
  const targets = [{ id: site, loc: base, base: '' }];
  if (federated) {
    try {
      const fed = await loadFederation(base);
      for (const s of fed) {
        if (s.id === site || !s.manifest) continue;
        if (targets.some((t) => t.id === s.id)) continue;
        targets.push({ id: s.id, loc: s.manifest, base: s.base || '' });
      }
    } catch {
      /* federation is optional */
    }
  }
  if (sites && sites.length) {
    for (const id of sites) if (!targets.some((t) => t.id === id)) targets.push({ id, loc: base, base: '' });
  }

  const loaded = await Promise.all(
    targets.map(async (t) => {
      try {
        return await loadSiteIndex(t.id, t.loc, t.base);
      } catch {
        return null;
      }
    }),
  );

  const groups = [];
  let total = 0;
  let anyLoaded = false;
  for (const entry of loaded) {
    if (!entry) continue;
    anyLoaded = true;
    const hits = searchDocs(term, entry.docs, 0);
    if (!hits.length) continue;
    const byShard = new Map();
    for (const h of hits) {
      const key = h.doc._shard || 'misc';
      if (!byShard.has(key)) {
        byShard.set(key, {
          site: entry.site,
          label: entry.label,
          siteBase: entry.base || '',
          shard: key,
          shardLabel: h.doc._shardLabel || key,
          hits: [],
        });
      }
      const external = entry.site !== site;
      const url = h.doc.url || '/';
      byShard.get(key).hits.push({
        id: h.doc.id,
        title: h.doc.title,
        text: h.doc.text,
        url,
        /** Absolute link for cross-site hits (SPA navigation cannot cross origin). */
        href: external ? `${(entry.base || '').replace(/\/+$/, '')}${url}` : url,
        image: h.doc.image,
        tags: h.doc.tags,
        score: h.score,
        external,
      });
    }
    for (const g of byShard.values()) {
      g.hits.sort((a, b) => b.score - a.score);
      total += g.hits.length;
      groups.push(g);
    }
  }

  // Own-site groups first, then siblings, then by best score inside each group.
  groups.sort((a, b) => {
    if ((a.site === site) !== (b.site === site)) return a.site === site ? -1 : 1;
    return (b.hits[0]?.score || 0) - (a.hits[0]?.score || 0);
  });

  const capped = [];
  let used = 0;
  for (const g of groups) {
    const room = Math.max(0, limit - used);
    if (room === 0) break;
    capped.push({ ...g, hits: g.hits.slice(0, room) });
    used += Math.min(g.hits.length, room);
  }

  return {
    query: term,
    groups: capped,
    total,
    error: anyLoaded ? null : 'index_unavailable',
  };
}

/** Flatten groups into a single ordered list (convenience for dropdowns). */
export function flattenGroups(groups, limit = 0) {
  const flat = [];
  for (const g of groups || []) {
    for (const h of g.hits || []) {
      flat.push({ ...h, site: g.site, siteLabel: g.label, siteBase: g.siteBase || '', shardLabel: g.shardLabel });
    }
  }
  flat.sort((a, b) => b.score - a.score);
  return limit > 0 ? flat.slice(0, limit) : flat;
}

/** Test/debug helper: drop memoised indexes. */
export function resetSearchCache() {
  cache.clear();
  inflight.clear();
  federationCache.clear();
}

/** Only render images the browser can actually resolve (S3 keys / bare ids are skipped). */
export function isRenderableImage(src) {
  if (!src || typeof src !== 'string') return false;
  return src.startsWith('/') || /^https?:/i.test(src) || src.startsWith('data:');
}
